import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const THEME_STORAGE_KEY = 'extk_onda_theme';
export const THEME_LIGHT = 'light';
export const THEME_DARK = 'dark';

const THEME_COLORS = {
  [THEME_LIGHT]: '#0D9488',
  [THEME_DARK]: '#0B1220'
};

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === THEME_LIGHT || stored === THEME_DARK) return stored;
  } catch {
    /* ignore */
  }
  return THEME_LIGHT;
}

export function applyThemeToDocument(theme) {
  const next = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;
  document.documentElement.dataset.theme = next;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', THEME_COLORS[next]);
  }
  return next;
}

const ThemeContext = createContext({
  theme: THEME_LIGHT,
  isDark: false,
  setTheme: () => {},
  toggleTheme: () => {}
});

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => readStoredTheme());

  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === THEME_DARK ? THEME_DARK : THEME_LIGHT);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === THEME_DARK ? THEME_LIGHT : THEME_DARK));
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === THEME_DARK,
      setTheme,
      toggleTheme
    }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
